/**
 * Airline -> terminal assignments for major US airports.
 *
 * Sourced from official airport terminal maps and airline "airport information"
 * pages (2025-2026). Terminal assignments DO change — every page built on this
 * data shows a "confirm on your boarding pass" note plus a link to the airport's
 * official site.
 */

export interface AirlineTerminalEntry {
  /** URL slug, e.g. "delta" */
  slug: string;
  /** Display name, e.g. "Delta Air Lines" */
  airline: string;
  /** Short name used in headlines, e.g. "Delta" */
  short: string;
  /** Where you check in / drop bags */
  terminal: string;
  /** Concourse or gate range, when the airport uses them */
  concourse?: string;
  /** Where international departures leave from, if different */
  international?: string;
  /** Anything travellers routinely get wrong */
  note?: string;
}

export interface AirportTerminalInfo {
  code: string;
  /** Official airport website for verification */
  officialUrl: string;
  /** How terminals connect (post-security or not) — the #1 follow-up question */
  connection: string;
  airlines: AirlineTerminalEntry[];
}

export const airportTerminals: Record<string, AirportTerminalInfo> = {
  JFK: {
    code: "JFK",
    officialUrl: "https://www.jfkairport.com/",
    connection:
      "JFK terminals are NOT connected after security. To change terminals you must exit, ride the AirTrain, and clear security again — allow at least 60 minutes.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 4", note: "Delta consolidated all JFK flights into Terminal 4 after Terminal 2 closed. Delta One check-in is at the north end of T4." },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 5", note: "JetBlue's Mint and international arrivals also use Terminal 5." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 8", note: "American moved out of Terminal 7 — all American flights now use Terminal 8." },
      { slug: "british-airways", airline: "British Airways", short: "British Airways", terminal: "Terminal 8", note: "British Airways relocated from Terminal 7 to Terminal 8 to co-locate with American." },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 8" },
      { slug: "emirates", airline: "Emirates", short: "Emirates", terminal: "Terminal 4" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 4" },
      { slug: "air-france", airline: "Air France", short: "Air France", terminal: "Terminal 1", note: "Terminal 1 is being redeveloped as the New Terminal One. Carrier locations here are shifting — check your boarding pass the day you fly." },
    ],
  },
  LGA: {
    code: "LGA",
    officialUrl: "https://www.laguardiaairport.com/",
    connection:
      "Terminals B and C are linked by a post-security pedestrian bridge. Terminal A is separate — you'd need the free shuttle on the arrivals level.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal C", note: "Delta's rebuilt Terminal C covers concourses D, E, F and G." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal B" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal B" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal B" },
      { slug: "air-canada", airline: "Air Canada", short: "Air Canada", terminal: "Terminal B" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal A", note: "Terminal A is the older Marine Air Terminal on the west side of the airport — budget extra time to reach it." },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal A" },
    ],
  },
  EWR: {
    code: "EWR",
    officialUrl: "https://www.newarkairport.com/",
    connection:
      "Newark's terminals are not connected after security. The AirTrain links all three terminals landside; allow 20-30 minutes to move between them.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal C", international: "Terminal C (gates C120-C139)", note: "United is Newark's hub carrier — nearly every United flight, domestic and international, leaves from Terminal C." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal A" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal A" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal A" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal A" },
    ],
  },
  LAX: {
    code: "LAX",
    officialUrl: "https://www.flylax.com/",
    connection:
      "Terminals 4 through 8 and Tom Bradley (TBIT) are connected after security. Terminals 1, 2 and 3 connect to each other and to TBIT via a post-security walkway.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminals 2 and 3", international: "Terminals 2 and 3, some at TBIT", note: "Delta uses Terminal 3 as its main LAX home, with additional gates in Terminal 2. Delta One check-in is in Terminal 3." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminals 4 and 5", international: "Terminal B (TBIT) for some long-haul flights" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminals 7 and 8" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 1" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 6" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 5" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 5" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 5" },
      { slug: "hawaiian", airline: "Hawaiian Airlines", short: "Hawaiian", terminal: "Terminal 5" },
    ],
  },
  ORD: {
    code: "ORD",
    officialUrl: "https://www.flychicago.com/ohare/",
    connection:
      "Terminals 1, 2 and 3 are connected after security by underground walkways. Terminal 5 is separate — reaching it means the free ATS train and a second security screening.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 1", concourse: "Concourses B and C", international: "Terminal 5 for some arrivals", note: "United's O'Hare hub is Terminal 1. The B and C concourses are linked by the lighted underground tunnel." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 3", concourse: "Concourses G, H, K and L", international: "Terminal 5 for some long-haul departures" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 5", note: "Delta moved from Terminal 2 to Terminal 5 — a common mistake for travellers going on old memory." },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 5", note: "Most Southwest Chicago flights leave from Midway (MDW), not O'Hare — double-check which airport you're booked from." },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 5" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 3" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 5" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 5" },
    ],
  },
  ATL: {
    code: "ATL",
    officialUrl: "https://www.atl.com/",
    connection:
      "Every concourse at Atlanta is connected after security by the Plane Train, which runs every couple of minutes. You only clear security once.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "South Terminal", concourse: "Concourses A, B, C and T", international: "Concourse E and F", note: "Delta check-in is the South Terminal. International departures leave from Concourse F, reachable by the Plane Train." },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "North Terminal", concourse: "Concourse C" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "North Terminal", concourse: "Concourse T" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "North Terminal", concourse: "Concourse T" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "North Terminal", concourse: "Concourse C" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "North Terminal", concourse: "Concourse C" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "North Terminal", concourse: "Concourse C" },
    ],
  },
  DFW: {
    code: "DFW",
    officialUrl: "https://www.dfwairport.com/",
    connection:
      "All five DFW terminals are connected after security by the free Skylink train. A full loop takes under 10 minutes.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminals A, B and C", international: "Terminal D", note: "American is DFW's hub carrier and uses four of the five terminals. Your terminal depends on the route — check your boarding pass." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal E" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal E" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal E" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal E" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal E" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal E" },
    ],
  },
  DEN: {
    code: "DEN",
    officialUrl: "https://www.flydenver.com/",
    connection:
      "Check-in for every airline is the Jeppesen Terminal (Level 6). Concourses A, B and C are all connected after security by the underground train; Concourse A also has a walkway bridge.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Jeppesen Terminal", concourse: "Concourse B", note: "United occupies essentially all of Concourse B, Denver's largest." },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Jeppesen Terminal", concourse: "Concourse C" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Jeppesen Terminal", concourse: "Concourse A" },
    ],
  },
  MCO: {
    code: "MCO",
    officialUrl: "https://orlandoairports.net/",
    connection:
      "Terminal A and Terminal B share one main building and one set of security checkpoints leading to airside gates 1-59 and 70-129. Terminal C is a separate building reached by the Terminal Link train.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal A", concourse: "Airside 2, gates 100-129" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal B", concourse: "Airside 4, gates 70-99" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal A", concourse: "Airside 4, gates 70-99" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal B", concourse: "Airside 4, gates 70-99" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal C", note: "JetBlue moved to the new Terminal C — a separate building. Don't head for A or B out of habit." },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal A", concourse: "Airside 1, gates 1-29" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal C" },
    ],
  },
  MIA: {
    code: "MIA",
    officialUrl: "https://www.miami-airport.com/",
    connection:
      "Miami's North, Central and South terminals form one continuous building. The Skytrain runs the length of the North Terminal after security; moving between concourse groups can mean re-clearing security.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "North Terminal", concourse: "Concourse D", note: "American occupies the entire North Terminal — Concourse D is over two miles end to end, so use the Skytrain." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "South Terminal", concourse: "Concourse H" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Central Terminal", concourse: "Concourse F" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "South Terminal", concourse: "Concourse H" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Central Terminal", concourse: "Concourse E" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Central Terminal", concourse: "Concourse G" },
    ],
  },
  SFO: {
    code: "SFO",
    officialUrl: "https://www.flysfo.com/",
    connection:
      "Terminals 1, 2, 3 and the International Terminal form a ring. Terminals 2 and 3 connect after security, as do Terminal 3 and International Terminal G. The AirTrain links everything landside.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 3", international: "International Terminal G", note: "United's domestic flights leave from Terminal 3 (Boarding Areas E and F); international departures use International Terminal G." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 1", concourse: "Boarding Area B" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 1", concourse: "Boarding Area B" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 1", concourse: "Boarding Area B" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 2", concourse: "Boarding Area D" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 1", concourse: "Boarding Area B" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 1" },
    ],
  },
  BOS: {
    code: "BOS",
    officialUrl: "https://www.massport.com/logan-airport/",
    connection:
      "Terminals B and C connect after security. Terminals A and E are reached landside via the free shuttle bus or the connector walkway.",
    airlines: [
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal C", international: "Terminal E for some international arrivals", note: "JetBlue is Logan's largest carrier and fills most of Terminal C." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal A", international: "Terminal E" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal B", international: "Terminal E" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal B" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal B" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal B" },
    ],
  },
  SEA: {
    code: "SEA",
    officialUrl: "https://www.portseattle.org/sea-tac",
    connection:
      "Sea-Tac is a single terminal. Concourses A-D are walkable after security; the N and S satellites are reached by the underground train.",
    airlines: [
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Main Terminal", concourse: "Concourses C and D, North Satellite", note: "Alaska is Sea-Tac's hub carrier and uses the rebuilt North Satellite plus Concourses C and D." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Concourses A and B", international: "South Satellite" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Main Terminal", concourse: "Concourse N / North Satellite" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Concourse B" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Main Terminal", concourse: "Concourse A" },
    ],
  },
  PHX: {
    code: "PHX",
    officialUrl: "https://www.skyharbor.com/",
    connection:
      "Terminals 3 and 4 are connected after security by a walkway. The PHX Sky Train links both terminals landside with parking and the light rail.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 4", note: "American is Sky Harbor's hub carrier and occupies most of Terminal 4." },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 4" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 3" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 3" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 3" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 3" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 3" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 3" },
    ],
  },
  LAS: {
    code: "LAS",
    officialUrl: "https://www.harryreidairport.com/",
    connection:
      "Terminal 1 and Terminal 3 are separate buildings with separate security. There is no post-security connection — go straight to the right terminal.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 1", concourse: "B and C gates", note: "Southwest is by far the biggest carrier at Harry Reid and fills much of Terminal 1." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 1", international: "Terminal 3" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 1" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 3" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 3" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 3" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 3" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 3" },
    ],
  },
  CLT: {
    code: "CLT",
    officialUrl: "https://www.cltairport.com/",
    connection:
      "Charlotte is a single terminal with concourses A through E all connected after security. You only clear security once.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Main Terminal", concourse: "Concourses B, C, D and E", international: "Concourse D and E", note: "American is Charlotte's hub carrier and operates the large majority of flights here." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Main Terminal", concourse: "Concourse A" },
    ],
  },
  IAH: {
    code: "IAH",
    officialUrl: "https://www.fly2houston.com/iah/",
    connection:
      "Terminals A through E are connected after security by the Skyway train. Terminal D (international) also connects, but the Subway train runs landside only.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminals A, B and C", international: "Terminal E", note: "United is Bush Intercontinental's hub carrier. Domestic flights spread across A, B and C; long-haul international leaves from Terminal E." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal A" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal A" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal A", note: "Most Southwest Houston flights use Hobby (HOU) instead — check which airport you're booked from." },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal A" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal A" },
    ],
  },
  DCA: {
    code: "DCA",
    officialUrl: "https://www.flyreagan.com/",
    connection:
      "Terminal 1 is a separate older building. Terminal 2's concourses D, E and F are connected after security via the National Hall security checkpoints.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 2", concourse: "Concourses D and E", note: "American is Reagan National's largest carrier." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 2", concourse: "Concourse C" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 2", concourse: "Concourse C" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 2", concourse: "Concourse C" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 2", concourse: "Concourse C" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 2", concourse: "Concourse C" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 1" },
    ],
  },
  MSP: {
    code: "MSP",
    officialUrl: "https://www.mspairport.com/",
    connection:
      "Terminal 1 and Terminal 2 are separate buildings roughly two miles apart, linked by the light rail. There is no post-security connection.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 1 (Lindbergh)", note: "Delta is the Minneapolis hub carrier and dominates Terminal 1." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 1 (Lindbergh)" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 1 (Lindbergh)" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 1 (Lindbergh)" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 2 (Humphrey)", note: "Terminal 2 is a completely separate building — do not go to Terminal 1 by mistake." },
      { slug: "sun-country", airline: "Sun Country Airlines", short: "Sun Country", terminal: "Terminal 2 (Humphrey)" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 2 (Humphrey)" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 2 (Humphrey)" },
    ],
  },
  DTW: {
    code: "DTW",
    officialUrl: "https://www.metroairport.com/",
    connection:
      "The McNamara Terminal and the Evans (North) Terminal are separate buildings with separate security. A shuttle bus links them landside.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "McNamara Terminal", concourse: "Concourses A, B and C", note: "Delta is Detroit's hub carrier. The ExpressTram runs the length of Concourse A." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Evans (North) Terminal" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Evans (North) Terminal" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Evans (North) Terminal" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Evans (North) Terminal" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "McNamara Terminal" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Evans (North) Terminal" },
    ],
  },
  FLL: {
    code: "FLL",
    officialUrl: "https://www.broward.org/airport/",
    connection:
      "Fort Lauderdale's four terminals are linked by a walkway. Terminals 1, 2 and 3 connect after security; Terminal 4 requires going landside.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 1", concourse: "Concourses B and C" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 2", concourse: "Concourse D" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 3", concourse: "Concourse E" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 3", concourse: "Concourse F" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 4", concourse: "Concourse G" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 1", concourse: "Concourse C" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 1", concourse: "Concourse A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 1", concourse: "Concourse C" },
    ],
  },
  PHL: {
    code: "PHL",
    officialUrl: "https://www.phl.org/",
    connection:
      "Terminals A through F are all connected after security by walkways, though the F concourse is a longer walk or a shuttle ride.",
    airlines: [
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminals A, B and C", international: "Terminal A-West", note: "American is Philadelphia's hub carrier. Regional flights often leave from Terminal F." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal D" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal D" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal E" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal E" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal E" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal E" },
    ],
  },
  SAN: {
    code: "SAN",
    officialUrl: "https://www.san.org/",
    connection:
      "Terminal 1 and Terminal 2 are not connected after security. A free shuttle runs between them on the arrivals level.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal 1", note: "Southwest is San Diego's largest carrier." },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal 1" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal 1" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal 2" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal 2" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal 2" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Terminal 2" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal 2" },
    ],
  },
  TPA: {
    code: "TPA",
    officialUrl: "https://www.tampaairport.com/",
    connection:
      "Tampa uses one main terminal with four airsides (A, C, E, F) reached by automated shuttles. Airsides are not connected to each other after security.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Airside C" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Airside E" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Main Terminal", concourse: "Airside C" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Main Terminal", concourse: "Airside E" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Main Terminal", concourse: "Airside A" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Main Terminal", concourse: "Airside A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Main Terminal", concourse: "Airside A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Main Terminal", concourse: "Airside E" },
    ],
  },
  SLC: {
    code: "SLC",
    officialUrl: "https://slcairport.com/",
    connection:
      "Salt Lake City's rebuilt airport has one terminal with Concourses A and B connected after security by an underground tunnel.",
    airlines: [
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Concourses A and B", note: "Delta is the Salt Lake City hub carrier and uses most gates in both concourses." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Main Terminal", concourse: "Concourse A" },
    ],
  },
  BWI: {
    code: "BWI",
    officialUrl: "https://www.bwiairport.com/",
    connection:
      "Concourses A through E are largely connected after security, with a walkway linking the A/B and C/D piers.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Concourses A and B", note: "Southwest is BWI's dominant carrier and fills both A and B concourses." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Concourse D" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Concourse D" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Concourse D" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Concourse D" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Concourse D" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Concourse D" },
    ],
  },
  IAD: {
    code: "IAD",
    officialUrl: "https://www.flydulles.com/",
    connection:
      "All Dulles concourses connect after security via the underground AeroTrain. Concourses A and B are also reachable on foot from the main terminal.",
    airlines: [
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Concourses C and D", international: "Concourse C", note: "United is Dulles' hub carrier. Allow time for the AeroTrain ride out to C/D." },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Concourse B" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Concourse B" },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Concourse A" },
      { slug: "jetblue", airline: "JetBlue Airways", short: "JetBlue", terminal: "Concourse A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Concourse B" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Concourse A" },
    ],
  },
  MDW: {
    code: "MDW",
    officialUrl: "https://www.flychicago.com/midway/",
    connection:
      "Midway is a single terminal with Concourses A, B and C all connected after one security checkpoint.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Concourses A and B", note: "Southwest operates the overwhelming majority of Midway flights." },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "porter", airline: "Porter Airlines", short: "Porter", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Main Terminal", concourse: "Concourse A" },
      { slug: "allegiant", airline: "Allegiant Air", short: "Allegiant", terminal: "Main Terminal", concourse: "Concourse A" },
    ],
  },
  PDX: {
    code: "PDX",
    officialUrl: "https://www.flypdx.com/",
    connection:
      "Portland has one terminal. Concourses B, C, D and E are connected after security through the new main hall.",
    airlines: [
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Main Terminal", concourse: "Concourses B and C", note: "Alaska and its Horizon regional flights are Portland's largest operation." },
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Main Terminal", concourse: "Concourse E" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Main Terminal", concourse: "Concourse E" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Main Terminal", concourse: "Concourse E" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Main Terminal", concourse: "Concourse E" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Main Terminal", concourse: "Concourse E" },
    ],
  },
  SAT: {
    code: "SAT",
    officialUrl: "https://www.flysanantonio.com/",
    connection:
      "Terminal A and Terminal B are connected both landside and after security by a short walkway.",
    airlines: [
      { slug: "southwest", airline: "Southwest Airlines", short: "Southwest", terminal: "Terminal A" },
      { slug: "american", airline: "American Airlines", short: "American", terminal: "Terminal A" },
      { slug: "delta", airline: "Delta Air Lines", short: "Delta", terminal: "Terminal A" },
      { slug: "united", airline: "United Airlines", short: "United", terminal: "Terminal A" },
      { slug: "alaska", airline: "Alaska Airlines", short: "Alaska", terminal: "Terminal B" },
      { slug: "frontier", airline: "Frontier Airlines", short: "Frontier", terminal: "Terminal A" },
      { slug: "spirit", airline: "Spirit Airlines", short: "Spirit", terminal: "Terminal A" },
    ],
  },
};

/** Airports that have terminal data, in a stable order for hub pages and sitemaps. */
export const terminalAirportCodes = Object.keys(airportTerminals);

export const getTerminalInfo = (code?: string): AirportTerminalInfo | undefined =>
  code ? airportTerminals[code.toUpperCase()] : undefined;

export const getAirlineEntry = (
  code?: string,
  slug?: string
): AirlineTerminalEntry | undefined => {
  if (!code || !slug) return undefined;
  return airportTerminals[code.toUpperCase()]?.airlines.find(
    (a) => a.slug === slug.toLowerCase()
  );
};

/** Every airport/airline pair — used to build the sitemap. */
export const allTerminalPairs = (): { code: string; slug: string }[] =>
  terminalAirportCodes.flatMap((code) =>
    airportTerminals[code].airlines.map((a) => ({ code, slug: a.slug }))
  );

/* ------------------------------------------------------------------ *
 * Terminal-number layer
 *
 * People search "jfk terminal 8", "o'hare terminal 1", "lga terminal b"
 * far more than they search by airline. These groups are derived from the
 * airline assignments above so the two views can never disagree.
 * ------------------------------------------------------------------ */

export interface TerminalGroup {
  /** URL slug, e.g. "terminal-8" */
  slug: string;
  /** Label exactly as the airport signs it, e.g. "Terminal 8" */
  label: string;
  /** Airlines that check in here */
  airlines: AirlineTerminalEntry[];
}

/** "Terminal 8" -> "terminal-8", "Concourse B" -> "concourse-b" */
export const terminalSlug = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Terminal groups for an airport, in first-seen order. */
export const getTerminalGroups = (code?: string): TerminalGroup[] => {
  const info = getTerminalInfo(code);
  if (!info) return [];

  const groups: TerminalGroup[] = [];
  info.airlines.forEach((a) => {
    const label = a.terminal.trim();
    if (!label) return;
    const slug = terminalSlug(label);
    const existing = groups.find((g) => g.slug === slug);
    if (existing) {
      existing.airlines.push(a);
    } else {
      groups.push({ slug, label, airlines: [a] });
    }
  });

  return groups.sort((a, b) => a.label.localeCompare(b.label, "en", { numeric: true }));
};

export const getTerminalGroup = (
  code?: string,
  slug?: string
): TerminalGroup | undefined => {
  if (!slug) return undefined;
  const wanted = slug.toLowerCase();
  return getTerminalGroups(code).find((g) => g.slug === wanted);
};

/** Every airport/terminal-number pair — used to build the sitemap. */
export const allTerminalNumberPairs = (): { code: string; slug: string }[] =>
  terminalAirportCodes.flatMap((code) =>
    getTerminalGroups(code).map((g) => ({ code, slug: g.slug }))
  );

